import { KeyValue, KeyValuePipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { ENV, LoggerService } from "@bequeathed/ngx-app-core";
import { MessageService, Navigator } from "@bequeathed/ngx-services";
import { Topic } from "@bequeathed/ngx-services/lib/model/ai-interview.model";
import { NgbCollapse } from "@ng-bootstrap/ng-bootstrap";

const LOG: LoggerService = LoggerService.get('NavigatorComponent');

/**
 * Displays a navigation panel on the left hand-side of the interview
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'navigator',
    templateUrl: './navigator.component.html',
    standalone: true,
    imports: [
        KeyValuePipe,
        NgbCollapse
    ],
    styleUrls: ['./navigator.component.sass']
})
export class NavigatorComponent implements OnInit {

    @Input() type: string | undefined
    @Input() editing: boolean | undefined
    @Input() navigator: Navigator | undefined

    /**
     * The "latest" / current outstanding topic id the user was on before jumping
     * back to edit an earlier answer (see the `interview-backward-navigation` spec,
     * "return to latest").
     *
     * While editing, the server sets `navigator.currentTopic` to the EDIT topic, so
     * the genuinely-latest topic is not `completed` and thus not offered by
     * {@link isEligibleEditTarget}. This input lets the navigator additionally offer
     * that topic as a "return to latest" target ({@link isReturnToLatest}) so the
     * user can go back without being forced to change an answer.
     *
     * `null`/`undefined` when not editing — the navigator then renders exactly as
     * before and the current topic is not turned into a jump button.
     */
    @Input() latestTopicId: string | null | undefined

    /**
     * Emitted when the user activates the return control on an eligible topic.
     * Carries the target `Topic.id` (`editTopicId`) for the edit flow.
     *
     * Task 5.1 wires the emission only; consumers do not act on it yet (see task 8).
     */
    @Output() jumpToTopic = new EventEmitter<string>()

    public messages = inject(MessageService)
    private title = inject(Title)

    currentTopic: Topic | undefined
    interviewFinished = false
    navigatorCollapsed = true
    progress = 0.0

    ngOnInit(): void {
        this.currentTopic = this.navigator!.topics!.get(this.navigator!.currentTopic!);
        this.progress = this.currentTopic!.progress!;

        this.title.setTitle(`Will Interview${!this.currentTopic ? '' : ' : ' + this.messages.getText(`${this.type}.topic.${this.currentTopic.id}`)}`);
    }

    get progressAsString(): string {
        LOG.debug('progressAsString', 'Fetching progress to display')
        return `${Math.ceil(this.progress)}`;
    }

    /**
     * Tooltip / supplementary text for a topic row, sourced entirely from
     * `MessageService` (no hard-coded strings).
     *
     * - The current topic shows the "you are here" tooltip.
     * - Any other topic has no tooltip text (empty string), so nothing extraneous
     *   is exposed. Accessible names for interactive/locked topics come from the
     *   dedicated `return-to-topic` / `locked-topic` aria-labels on the controls.
     *
     * @param topic the topic to describe
     */
    getTooltipText(topic: Topic): string {
        if (this.currentTopic?.id === topic.id) {
            return this.messages.getText('WILL.interview.you-are-here');
        }
        return this.messages.getText(`WILL.topic.${topic.id}`);
    }

    /**
     * Whether the given topic is an eligible target for backward navigation / editing.
     *
     * A topic is eligible when it is completed, not immutable, and not the current topic.
     * `immutable` being undefined is treated as editable (i.e. not immutable).
     *
     * Groundwork only: this does not yet make topics interactive (see task 5).
     *
     * @param topic the topic to evaluate
     * @returns true if the topic can be returned to and edited
     */
    isEligibleEditTarget(topic: Topic): boolean {
        return !!topic.completed && !topic.immutable && this.currentTopic?.id !== topic.id;
    }

    /**
     * Feature flag guarding backward navigation.
     *
     * Read from the runtime config map (`ENV.config`, populated from config-services),
     * mirroring the existing boolean-flag pattern (e.g. `willDownloadEnabled` in the
     * home app). When false/absent, the navigator renders exactly as before.
     *
     * This flag is temporary and is removed once the feature is enabled by default
     * (see task 12).
     */
    get backwardNavigationEnabled(): boolean {
        return ENV.config.get('backward-navigation-enabled') === true ||
                ENV.config.get('backward-navigation-enabled') === 'true';
    }

    /**
     * Whether the given topic should be rendered as an interactive return control.
     *
     * True only when the feature flag is on AND the topic is an eligible edit target.
     *
     * @param topic the topic to evaluate
     */
    isNavigable(topic: Topic): boolean {
        return this.backwardNavigationEnabled &&
            (this.isEligibleEditTarget(topic) || this.isReturnToLatest(topic));
    }

    /**
     * Whether the given topic is the "return to latest" target — the current /
     * latest outstanding topic the user was on before jumping back to edit an
     * earlier answer (see the `interview-backward-navigation` spec, "return to
     * latest").
     *
     * The latest topic is NOT `completed` (it hasn't been answered yet), which is
     * exactly why {@link isEligibleEditTarget} excludes it; this helper covers it
     * explicitly so the user can return to it WITHOUT making any change.
     *
     * True when: the feature flag is on, the topic is NOT immutable, the topic id
     * equals {@link latestTopicId}, and it is NOT the current (edit) topic. When the
     * user is NOT editing, `currentTopic` IS the latest, so `id === latestTopicId`
     * and `id !== currentTopic` cannot both hold — the current topic is never turned
     * into a jump button in the normal forward flow.
     *
     * @param topic the topic to evaluate
     */
    isReturnToLatest(topic: Topic): boolean {
        return this.backwardNavigationEnabled &&
            !topic.immutable &&
            !!this.latestTopicId &&
            topic.id === this.latestTopicId &&
            this.currentTopic?.id !== topic.id;
    }

    /**
     * Whether the given topic should be rendered as "completed-but-locked".
     *
     * True only when the feature flag is on AND the topic is completed AND immutable.
     * Locked topics are shown with a lock icon plus accompanying text (state conveyed
     * by more than colour alone, per accessibility requirement 7.5) and are NOT
     * interactive.
     *
     * @param topic the topic to evaluate
     */
    isLocked(topic: Topic): boolean {
        return this.backwardNavigationEnabled && !!topic.completed && !!topic.immutable;
    }

    /**
     * Handle activation of a return control.
     *
     * Task 5.1: emit the target topic id only. No navigation is wired yet — that is
     * task 8 (the consumer shows the confirmation and enters edit mode). This is a
     * deliberate no-op beyond emitting the event.
     *
     * @param topic the topic the user wants to return to
     */
    onJumpToTopic(topic: Topic): void {
        LOG.debug('onJumpToTopic', `Requested return to topic [${topic.id}]`);
        this.jumpToTopic.emit(topic.id);
    }

    sortByOrder = (a: KeyValue<string, Topic>, b: KeyValue<string, Topic>) => {
        return a.value!.order! - b.value!.order!;
    }

}
